import { useState, useRef, useEffect } from "react";
import Sidebar from "../Sidebar/Sidebar";
import Place from "../Hotspot/Place";
import Facility from "../Hotspot/Facility";
import useWindowWidth from "../../hooks/useWindowWidth";
import { swipeIcon } from "../Icons/Icons";
import map from "../../assets/images/map.jpg";
import ZoomPanel from "../ZoomPanel/ZoomPanel";
import CookieConsent from "../CookieConsent/CookieConsent";
//Services
import BrowserStorageService from "../../services/BrowserStorageService";
import ScriptService from "../../services/ScriptService";
import ZoomService from "../../services/ZoomService";

function Map() {
    const [wonderlandLocations, setWonderlandLocations] = useState(null);
    const [modalVisible, setModalVisible] = useState(null);
    const [userHasScrolled, setUserHasScrolled] = useState(false);
    const [hotspotHighlight, setHotspotHighlight] = useState(null);
    const [animationsActive, setAnimationsActive] = useState(true);
    const [showSidebar, setShowSidebar] = useState(false);
    const mainWrapper = useRef();
    const mapRef = useRef();
    const isDesktop = useWindowWidth(992);

    useEffect(() => {
        if (wonderlandLocations) return;

        async function getPublicContent() {
            await ScriptService.loadData(`/configuration/places.js`).catch(e => console.error(e));
            setWonderlandLocations({
                places: window.wonderlandPlaces,
                facilities: window.wonderlandFacilities
            });
        }

        getPublicContent();
    }, [wonderlandLocations]);

    useEffect(() => {
        const userPreference = BrowserStorageService.getLocalStorageData("animUserPreference", true);

        if (userPreference !== null) {
            setAnimationsActive(userPreference);
        }
    }, []);

    function handleHotspotAssigning(order, origin) {
        origin = !origin || (origin.toLowerCase() !== "map" && origin.toLowerCase() !== "sidebar") ? null : origin;
        setHotspotHighlight({
            order,
            origin
        });
    }

    function handleScroll() {
        if (userHasScrolled) return;
        setUserHasScrolled(true);
    }

    function scrollToPosition(num) {
        if (isDesktop) return;
        if (!userHasScrolled) handleScroll();

        let { x, y } = wonderlandLocations.places[num].interactiveContent.hotspot.mobile;
        let { width, height } = mainWrapper.current.getBoundingClientRect();
        let halfDot = 20;

        let newX = x - (width / 2) + halfDot;
        let newY = y - (height / 2) + halfDot;

        ZoomService.resetZoom();
        ZoomService.resetPosition();
        ZoomService.updateOffsetX(-Math.abs(newX));
        ZoomService.updateOffsetY(-Math.abs(newY));
        ZoomService.applyTransformations();
        ZoomService.handleAdjustment();
    }

    function toggleAnimations() {
        if (animationsActive) {
            setAnimationsActive(false);
            BrowserStorageService.setLocalStorageData("animUserPreference", false);
        } else {
            setAnimationsActive(true);
            BrowserStorageService.setLocalStorageData("animUserPreference", true);
        }
    }

    return wonderlandLocations?.places ? (
        <>
            <div className={`mw-map-grid-container ${!animationsActive ? "disable-animations" : ""}`} id="map-grid-container">
                <Sidebar
                    places={wonderlandLocations.places}
                    onSetModalVisible={setModalVisible}
                    onScrollToPosition={scrollToPosition}
                    handleHotspotAssigning={handleHotspotAssigning}
                    showSidebar={showSidebar}
                    setShowSidebar={setShowSidebar}
                    isDesktop={isDesktop}
                />

                <main
                    className="mw-map-main"
                    ref={mainWrapper}
                    onPointerDown={handleScroll}
                >
                    {!userHasScrolled &&
                        <div className="swipe-message">
                            <span className="swipe-icon">{swipeIcon}</span>
                            <span className="swipe-label">Swipe to discover</span>
                        </div>
                    }
                    <div className="mw-map-inner" ref={mapRef} style={{height: showSidebar ? "100vh" : ""}}>

                        <img src={map} alt="Wonderland Map" />

                        {(isDesktop || userHasScrolled) &&
                            Object.keys(wonderlandLocations.places).map((place, i) => (
                                wonderlandLocations.places[place].displayInMap &&
                                    <Place
                                        key={i}
                                        name={wonderlandLocations.places[place].name}
                                        order={place}
                                        content={wonderlandLocations.places[place].interactiveContent}
                                        modalVisible={modalVisible}
                                        onSetModalVisible={setModalVisible}
                                        handleHotspotAssigning={handleHotspotAssigning}
                                        hotspotHighlight={hotspotHighlight}
                                        isDesktop={isDesktop}
                                    />
                            ))
                        }
                        {(isDesktop || userHasScrolled) &&
                            wonderlandLocations.facilities.map((facility, i) => (
                                facility.displayInMap &&
                                    <Facility
                                        key={i}
                                        icon={facility.icon}
                                        position={facility.position}
                                    />
                            ))
                        }
                    </div>
                </main>

                {(isDesktop || userHasScrolled) && (
                    <button
                        onClick={toggleAnimations}
                        className={`toggle-animations-button ${!animationsActive ? "animations-disabled" : ""}`}
                        aria-hidden="true"
                    >
                        <svg version="1.1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 30 30">
                            {animationsActive &&
                                <path d="M12.27,21.71c-0.39,0-0.75-0.14-0.99-0.38L6.35,16.4c-0.42-0.46-0.53-1.07-0.29-1.61c0.25-0.57,0.82-0.96,1.42-0.96
                                    c0.37,0,0.72,0.15,1.02,0.42l0.01,0.01l3.75,3.84l9.06-8.89c0.29-0.31,0.64-0.47,1.02-0.47c0.6,0,1.18,0.41,1.41,1
                                    c0.21,0.54,0.07,1.11-0.38,1.52L13.32,21.34l-0.01,0.01C13.02,21.58,12.65,21.71,12.27,21.71z"/>
                            }
                            <path d="M15.05,30c-4.04,0-7.82-1.55-10.66-4.38C1.56,22.81,0,19.05,0,15.05c0-4.04,1.56-7.83,4.39-10.66
                                C7.22,1.56,11.01,0,15.05,0c4,0,7.76,1.56,10.57,4.39C28.45,7.23,30,11.01,30,15.05C30,23.29,23.29,30,15.05,30z M15.05,2.71
                                c-6.8,0-12.34,5.54-12.34,12.34c0,6.75,5.54,12.24,12.34,12.24c6.75,0,12.24-5.49,12.24-12.24C27.29,8.24,21.8,2.71,15.05,2.71z"/>
                        </svg>
                        <span>Animations {animationsActive ? "on" : "off"}</span>
                    </button>
                )}

                {!isDesktop && (userHasScrolled || showSidebar) && <ZoomPanel map={mapRef.current}  />}
                <CookieConsent></CookieConsent>
            </div>
        </>
    ) : (
        <div>Fetching content</div>
    );
}

export default Map;
